import * as React from "react";
import {useContext} from "react";
import {globalSettings} from "./config";
import {GlobalInfo} from "./context";
import {numberWithCommas, parseNumber} from "./util";
import {toast} from "react-toastify";
import {ISK_ADJUST, ISK_CREDIT, ISK_DEBIT, modifyBalance, PANDA_ADJUST, PANDA_CREDIT, PANDA_THEN_ISK} from "./model/transaction";
import {Dropdown, DropdownButton} from "react-bootstrap";
import {ACCOUNTS} from "./model/audit";
import {postToDiscord} from "./discord";
import { useTranslation } from 'react-i18next';
import { usePromptModalContext } from './PromptModal';

import { DropdownSubmenu, NavDropdownMenu} from "react-bootstrap-submenu";

export const CAPITAL_COMPONENTS = {
  'Capital Armor Plates': true,
  'Capital Capacitor Battery': true,
  'Capital Computer System': true,
  'Capital Construction Parts': true,
  'Capital Corporate Hangar Bay': true,
  'Capital Drone Bay': true,
  'Capital Jump Drive': true,
  'Capital Power Generator': true,
  'Capital Propulsion Engine': true,
  'Capital Sensor Cluster': true,
  'Capital Shield Emitter': true,
  'Capital Ship Maintenance Bay': true,
  'Capital Launcher Hardpoint': true,
  'Capital Siege Array': true,
  'Capital Cargo Bay': true,
  'Capital Clone Vat Bay': true,
  'Capital Doomsday Weapon Mount': true,
  'Capital Turret Hardpoint': true,
  'Capital Jump Bridge Array': true
};

export function CapitalComponentsTable(props) {
  const context = useContext(GlobalInfo);
  const { t } = useTranslation();
  const { recipe } = props;

  if (!recipe) return null;

  const components = props.components || {};

  const getRequiredAmount = (quantity, isComponent = true) => {
    // Values passed in already have efficiency applied from calculateComponents()
    // Just return the value as-is
    return quantity;
  };

  const getComponentCosts = (name, required) => {
    const componentRecipe = context.recipes.find(r => r.name === name);
    if (!componentRecipe) return { productionCost: 0, materialCost: 0 };

    // Get production cost discount (20% from skills)
    const prodCostDiscount = context.globalSettings?.componentProductionCostDiscount || 0.20;
    const prodCostMultiplier = 1 - prodCostDiscount;  // 0.80

    // Blueprint + Production cost (with 20% discount on production cost)
    const productionCost = ((componentRecipe.raw_manufacture_cost || 0) * prodCostMultiplier +
                          (componentRecipe.blueprint_price || componentRecipe.isk || 0)) * required;

    // Material costs - apply 84% efficiency for component materials (DB has raw pre-efficiency values), no markup
    const efficiency = context.globalSettings?.shipTypeToMaterialEfficiency['component'] || 0.84;
    let materialCost = 0;
    if (componentRecipe.materials) {
      Object.entries(componentRecipe.materials).forEach(([matName, amount]) => {
        const material = context.materialsMap[matName];
        if (material && material.price) {
          materialCost += material.price * amount * efficiency;
        }
      });
    }
    materialCost *= required;

    return { productionCost, materialCost };
  };

  // Check if a ship type is a capital ship
  const isCapitalShipType = (shipType) => {
    const capitalTypes = ['Carriers', 'Dreadnoughts', 'Force Auxiliaries', 'Freighters',
                          'Jump Freighters', 'Capital Industrial Ship', 'Lancer',
                          'Tactical Carrier', 'Supercarrier', 'Versatile Assault'];
    return capitalTypes.includes(shipType);
  };

  const getShipCosts = (name, required) => {
    // For ships used as materials, use the same calculation as the main corp price
    const shipRecipe = context.recipes.find(r => r.name === name);
    if (shipRecipe) {
      // Use the exact same logic as calculatePrice from RecipesPage.js
      if (isCapitalShipType(shipRecipe.ship_type)) {
        // For capital ships, use the same special logic as corp price calculation
        let totalCost = 0;

        // Get production cost discount (20% from skills)
        const prodCostDiscount = context.globalSettings?.componentProductionCostDiscount || 0.20;
        const prodCostMultiplier = 1 - prodCostDiscount;  // 0.80

        // Calculate capital component costs - apply 84% efficiency for component materials (DB has raw values), no markup
        const capitalEfficiency = context.globalSettings?.shipTypeToMaterialEfficiency['component'] || 0.84;
        Object.entries(shipRecipe.materials || {}).forEach(([materialName, quantity]) => {
          if (CAPITAL_COMPONENTS[materialName]) {
            const componentRecipe = context.recipes.find(r => r.name === materialName);
            if (componentRecipe) {
              // Apply 20% production cost discount
              const productionCost = ((componentRecipe.raw_manufacture_cost || 0) * prodCostMultiplier +
                                    (componentRecipe.blueprint_price || componentRecipe.isk || 0)) * quantity;

              let materialCost = 0;
              if (componentRecipe.materials) {
                Object.entries(componentRecipe.materials).forEach(([matName, amount]) => {
                  const material = context.materialsMap[matName];
                  if (material && material.price) {
                    // Apply 84% efficiency - DB has raw pre-efficiency values
                    materialCost += material.price * amount * capitalEfficiency;
                  }
                });
                materialCost *= quantity;
              }

              totalCost += productionCost + materialCost;
            }
          }
        });
        
                 // Also calculate cost for non-component materials (like minerals)
         Object.entries(shipRecipe.materials || {}).forEach(([materialName, quantity]) => {
           if (!CAPITAL_COMPONENTS[materialName]) {
             const material = context.materialsMap[materialName];
             if (material && material.price) {
               let materialCost = 0;
               if (material.type === 'mineral' || material.type === 'planetary') {
                 const efficiency = context.globalSettings?.shipTypeToMaterialEfficiency['capital'] || 0.84;
                 materialCost = (material.price || 0) * quantity * efficiency * (context.globalSettings?.purchasePriceMaterialCostRatio || 1.15);
               } else if (material.type === 'ore') {
                 return; // Skip ores
               } else {
                 materialCost = (material.price || 0) * quantity * (context.globalSettings?.purchasePriceMaterialCostRatio || 1.15);
               }
               totalCost += materialCost;
             }
           }
         });
        
        // Add base production and blueprint costs
        totalCost += (shipRecipe.raw_manufacture_cost || shipRecipe.isk || 0);
        totalCost += (shipRecipe.blueprint_price || 0);
        
        return { productionCost: totalCost * required, materialCost: 0 };
      } else {
        // For non-capital ships, calculate using material breakdown
        let totalShipCost = 0;
        
        Object.entries(shipRecipe.materials || {}).forEach(([materialName, quantity]) => {
          const material = context.materialsMap[materialName];
          if (material && material.price) {
            const efficiency = context.globalSettings?.shipTypeToMaterialEfficiency[shipRecipe.ship_type] || 0.89;
            totalShipCost += material.price * quantity * efficiency * (context.globalSettings?.purchasePriceMaterialCostRatio || 1.15);
          }
        });
        
        // Add base production and blueprint costs
        totalShipCost += (shipRecipe.raw_manufacture_cost || shipRecipe.isk || 0);
        totalShipCost += (shipRecipe.blueprint_price || 0);
        
        return { productionCost: totalShipCost * required, materialCost: 0 };
      }
    }
    
    // If no recipe found, use material price
    const material = context.materialsMap[name];
    const materialCost = (material?.price || 0) * required;
    return { productionCost: 0, materialCost };
  };

  const getMaterialCosts = (name, required) => {
    // For regular materials (like Morphite)
    const material = context.materialsMap[name];
    const materialCost = (material?.price || 0) * required * (context.globalSettings?.purchasePriceMaterialCostRatio || 1.15);
    return { productionCost: 0, materialCost };
  };

  // Get all materials including non-components
  const getAllMaterials = () => {
    const allMaterials = { ...components };
    
    // Add non-component materials from recipe
    if (recipe.materials) {
      Object.entries(recipe.materials).forEach(([name, quantity]) => {
        if (!CAPITAL_COMPONENTS[name]) {
          // This is a non-component material (ship, mineral, etc.)
          allMaterials[name] = quantity;
        }
      });
    }
    
    return allMaterials;
  };

  let totalProductionCost = 0;
  let totalMaterialCost = 0;

  const allMaterials = getAllMaterials();

  return (
    <table className="table table-sm table-bordered capital-components">
      <thead>
        <tr className="component-row bg-dark">
          <th style={{width: '25%'}}>{t('common:components.tableHeaders.componentsAndMaterials')}</th>
          <th style={{width: '15%'}}>{t('common:components.tableHeaders.required')}</th>
          <th style={{width: '30%'}}>{t('common:components.tableHeaders.blueprintProductionCost')}</th>
          <th style={{width: '30%'}}>{t('common:components.tableHeaders.materialCost')}</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(allMaterials)
          .sort(([nameA], [nameB]) => {
            // Sort capital components first, then other materials
            const aIsComponent = CAPITAL_COMPONENTS[nameA];
            const bIsComponent = CAPITAL_COMPONENTS[nameB];
            if (aIsComponent && !bIsComponent) return -1;
            if (!aIsComponent && bIsComponent) return 1;
            return nameA.localeCompare(nameB);
          })
          .map(([name, quantity]) => {
            const isCapitalComponent = CAPITAL_COMPONENTS[name];
            const material = context.materialsMap[name];
            const isShip = material?.type === 'ship';
            
            let required, productionCost, materialCost;
            
            if (isCapitalComponent) {
              // Capital component
              required = getRequiredAmount(quantity, true);
              const costs = getComponentCosts(name, required);
              productionCost = costs.productionCost;
              materialCost = costs.materialCost;
            } else if (isShip) {
              // Ship material
              required = Math.ceil(quantity);
              const costs = getShipCosts(name, required);
              productionCost = costs.productionCost;
              materialCost = costs.materialCost;
            } else {
              // Regular material (mineral, planetary, etc.)
              required = getRequiredAmount(quantity, false);
              const costs = getMaterialCosts(name, required);
              productionCost = costs.productionCost;
              materialCost = costs.materialCost;
            }
            
            totalProductionCost += productionCost;
            totalMaterialCost += materialCost;

            const rowClass = isCapitalComponent ? "component-row bg-dark" : "material-row bg-secondary";
            const nameDisplay = isShip ? `${name} ${t('common:components.labels.ship')}` : isCapitalComponent ? name : `${name} ${t('common:components.labels.material')}`;

            return (
              <tr key={name} className={rowClass}>
                <td>{nameDisplay}</td>
                <td className="text-center">{numberWithCommas(required)}</td>
                <td className="text-right">
                  {productionCost > 0 ? `$ ${numberWithCommas(Math.round(productionCost))}` : '$ -'}
                </td>
                <td className="text-right">
                  {materialCost > 0 ? `$ ${numberWithCommas(Math.round(materialCost))}` : '$ -'}
                </td>
              </tr>
            );
          })}
        <tr className="component-row bg-dark font-weight-bold">
          <td colSpan="2" className="text-right">{t('common:components.labels.totals')}</td>
          <td className="text-right">$ {numberWithCommas(Math.round(totalProductionCost))}</td>
          <td className="text-right">$ {numberWithCommas(Math.round(totalMaterialCost))}</td>
        </tr>
      </tbody>
    </table>
  );
}

export function Toggle(props) {
  const [expand, setExpand] = React.useState(false);
  const context = useContext(GlobalInfo);
  const { t } = useTranslation();
  let debugInfo = null;

  if (props.recipe) {
    const recipe = props.recipe;
    const efficiency = context.globalSettings?.shipTypeToMaterialEfficiency[recipe.shipType || recipe.ship_type] || 1;
    const quantity = props.quantity || 1;

    // Helper to format an object for debug display
    const formatObject = (obj, indent = '') => {
      if (!obj) return 'null';
      return Object.entries(obj)
        .map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return `${indent}${key}: {\n${formatObject(value, indent + '  ')}\n${indent}}`;
          }
          return `${indent}${key}: ${JSON.stringify(value)}`;
        })
        .join('\n');
    };

    debugInfo = <div style={{fontFamily: 'monospace', whiteSpace: 'pre', marginLeft: '20px', color: '#666', marginBottom: '10px'}}>
      DEBUG:
      {'\nFull Recipe:'} 
      {`\n{\n${formatObject(recipe, '  ')}\n}`}
      {`\n\nEfficiency: ${efficiency}`}
      {`\nBase recipe cost: ${recipe.raw_manufacture_cost || recipe.isk}`}
      {`\nRecipe*efficiency: ${(recipe.raw_manufacture_cost || recipe.isk) * efficiency}`}
      
      {recipe.materials && Object.entries(recipe.materials).map(([name, amount]) => {
        const componentRecipe = context.recipes.find(r => r.name === name);
        if (componentRecipe && componentRecipe.ship_type === 'Components') {
          const compEfficiency = context.globalSettings?.shipTypeToMaterialEfficiency[componentRecipe.ship_type] || 1;
          const baseCost = componentRecipe.raw_manufacture_cost || componentRecipe.isk || 0;
          return `\n\nComponent ${name}:
Base cost: ${baseCost}
Efficiency: ${compEfficiency}
Base*efficiency: ${baseCost * compEfficiency}
Quantity needed: ${amount}
Total for component: ${baseCost * compEfficiency * amount}
Full Component Recipe:
{
${formatObject(componentRecipe, '    ')}
}`
        }
        return '';
      })}

      {`\n\nDirect Materials:`}
      {recipe.materials && Object.entries(recipe.materials).map(([name, amount]) => {
        const material = context.materialsMap[name];
        if (material && material.price) {
          return `\n${name}: ${amount} * ${material.price} = ${amount * material.price}
Full Material Info:
{
${formatObject(material, '    ')}
}`;
        }
        return '';
      })}
    </div>;
  }

  return <div>
    <a className={"show-materials"} onClick={(e) => {
      setExpand(!expand);
      e.preventDefault()
    }} href={"#"}>
      {expand ? t('common:buttons.hide', { title: props.title || "" }) : t('common:buttons.show', { title: props.title || "" })}
    </a>
    {expand && <>
      {debugInfo}
      {props.children}
    </>}
  </div>
}

export function If(props) {
  if (props.cond) {
    return props.children;
  } else {
    return null;
  }
}

export function FinanceOnly(props) {
  let context = useContext(GlobalInfo);
  return <If cond={context.roles.finance}>
    {props.children}
  </If>
}

export function EngineerOnly(props) {
  let context = useContext(GlobalInfo);
  return <If cond={context.roles.engineer}>
    {props.children}
  </If>
}

export function ShipComponentsTable(props) {
  const context = useContext(GlobalInfo);
  const { t } = useTranslation();
  const { recipe, materials, components } = props;
  console.log(recipe, materials, components);
  // Helper to calculate material value
  const calculateMaterialValue = (materials) => {
    return Object.entries(materials).reduce((total, [name, quantity]) => {
      const material = context.materialsMap[name];
      if (material && material.price) {
        // Components use same efficiency as modules
        const efficiency = context.globalSettings?.shipTypeToMaterialEfficiency['module'] || 0.89;
        return total + (material.price * quantity * efficiency);
      }
      return total;
    }, 0);
  };

  // Get all components and materials with their quantities
  const getItemsList = () => {
    const itemsList = [];
    
    // If we have components, only show those
    if (components && Object.keys(components).length > 0) {
      // Add components from components field
      Object.entries(components).forEach(([name, quantity]) => {
        const componentRecipe = context.recipes.find(r => r.name === name);
        if (componentRecipe) {
          itemsList.push({
            name,
            recipe: componentRecipe,
            quantity: Math.ceil(quantity),
            isComponent: true
          });
        }
      });
    } else {
      // Otherwise, show materials
      Object.entries(materials || {}).forEach(([name, quantity]) => {
        const componentRecipe = context.recipes.find(r => r.name === name);
        if (componentRecipe && componentRecipe.ship_type === 'Components') {
          // This is a component
          itemsList.push({
            name,
            recipe: componentRecipe,
            quantity: Math.ceil(quantity),
            isComponent: true
          });
        } else {
          // This is a regular material
          const material = context.materialsMap[name];
          if (material) {
            itemsList.push({
              name,
              material,
              quantity: Math.ceil(quantity),
              isComponent: false
            });
          }
        }
      });
    }

    // Sort items: components first, then materials
    itemsList.sort((a, b) => {
      if (a.isComponent === b.isComponent) {
        return a.name.localeCompare(b.name);
      }
      return a.isComponent ? -1 : 1;
    });

    return itemsList;
  };

  const itemsList = getItemsList();
  console.log(itemsList);
  const totalBlueprintCost = recipe.blueprint_price || 0;
  let totalProductionCost = 0;
  let totalMaterialCost = 0;

  const getInventoryCount = (name) => {
    const inventory = context.materialsMap[name]?.inventory?.[props.location] || 0;
    return inventory;
  };

  return (
    <table className="table table-sm table-bordered ship-components">
      {console.log("1")}
      <thead>
        <tr className="component-row bg-dark">
          <th style={{width: '40%'}}>{t('common:components.tableHeaders.componentsAndMaterials')}</th>
          <th style={{width: '10%'}}>{t('common:components.tableHeaders.required')}</th>
          <th style={{width: '25%'}}>{t('common:components.tableHeaders.productionCost')}</th>
          <th style={{width: '25%'}}>{t('common:components.tableHeaders.materialCost')}</th>
        </tr>
      </thead>
      <tbody>
        {itemsList.map((item) => {
          console.log(item);
          if (item.isComponent) {
            console.log("2");
            
            // Handle component
            const productionCost = (item.recipe.raw_manufacture_cost || item.recipe.isk || 0) * item.quantity;
            
            const materialCost = calculateMaterialValue(item.recipe.materials || {}) * item.quantity;
          
            
            totalProductionCost += productionCost;
            totalMaterialCost += materialCost;

            return (
              <tr key={item.name} className="component-row bg-dark">
                <td className="component-name">
                  {item.name}
                  <small className="text-muted ml-2">
                    {t('common:components.labels.inStock', { count: getInventoryCount(item.name) })}
                  </small>
                </td>
                <td className="text-center">{item.quantity}</td>
                <td className="text-right">{numberWithCommas(Math.round(productionCost))} ISK</td>
                <td className="text-right">{numberWithCommas(Math.round(materialCost))} ISK</td>
              </tr>
            );
            
          } else {
            console.log("3")
            // Handle material
            const materialCost = (item.material.price || 0) * item.quantity * globalSettings.purchasePriceMaterialCostRatio;
            totalMaterialCost += materialCost;

            return (
              <tr key={item.name} className="material-row bg-dark">
                <td className="material-name">
                  {item.name}
                  <small className="text-muted ml-2">
                    {t('common:components.labels.inStock', { count: getInventoryCount(item.name) })}
                  </small>
                </td>
                <td className="text-center">{numberWithCommas(item.quantity)}</td>
                <td className="text-right">-</td>
                <td className="text-right">{numberWithCommas(Math.round(materialCost))} ISK</td>
              </tr>
            );
          }
        })}
        <tr className="total-row bg-dark">
          <td>{t('common:components.labels.total')}</td>
          <td className="text-center"></td>
          <td className="text-right">{numberWithCommas(Math.round(totalProductionCost))} ISK</td>
          <td className="text-right">{numberWithCommas(Math.round(totalMaterialCost))} ISK</td>
        </tr>
        <tr className="blueprint-row bg-dark">
          <td>{t('common:components.labels.shipBlueprint')}</td>
          <td></td>
          <td className="text-right">{numberWithCommas(Math.round(totalBlueprintCost))} ISK</td>
          <td></td>
        </tr>
        <tr className="production-cost-row bg-dark">
          <td>{t('common:components.labels.shipProductionCost')}</td>
          <td></td>
          <td className="text-right">{numberWithCommas(Math.round(recipe.raw_manufacture_cost || recipe.isk || 0))} ISK</td>
          <td></td>
        </tr>
        <tr className="grand-total-row bg-dark font-weight-bold">
          <td>{t('common:components.labels.grandTotal')}</td>
          <td></td>
          <td colSpan="2" className="text-right">
            {numberWithCommas(Math.round(
              totalProductionCost +
              totalMaterialCost +
              totalBlueprintCost +
              (recipe.raw_manufacture_cost || recipe.isk || 0)
            ))} ISK
          </td>
        </tr>
      </tbody>
    </table>
  );
}

export function MaterialList(props) {
  let context = useContext(GlobalInfo);
  const { t } = useTranslation();
  if(!props.materials) {
    return null;
  }

  // If this is a top-level ship recipe with components, use the table view
  if (props.recipe && props.recipe.components) {
    return <ShipComponentsTable {...props} />;
  }

  let materialNames = Object.keys(props.materials);
  materialNames.sort((a, b) =>
      (context.materialsMap[a] && context.materialsMap[a].order) -
      (context.materialsMap[b] && context.materialsMap[b].order)
  );

  // Helper to calculate the total material value for a recipe
  const calculateMaterialValue = (materials) => {
    return Object.entries(materials).reduce((total, [name, quantity]) => {
      const material = context.materialsMap[name];
      if (material && material.price) {
        return total + (material.price * quantity * globalSettings.purchasePriceMaterialCostRatio);
      }
      return total;
    }, 0);
  };

  return <ul>
    {props.isk ? <li>{numberWithCommas(props.isk)} ISK</li> : null}
    {props.blueprintIsk ? <li>Blueprint ({numberWithCommas(props.blueprintIsk)} ISK)</li> : null}
    
    {/* Show Direct Materials section */}
    {materialNames.length > 0 && <li>
      <strong>{t('common:components.labels.directMaterials')}</strong>

      <ul>
        {materialNames.map(name => {
          if(props.materials[name] !== 0) {
            const recipe = context.recipes.find(r => r.name === name);
            const quantity = Math.ceil(props.materials[name]);
            
            if (recipe && recipe.ship_type === 'Components') {
              // Handle capital components
              const bakeFeePer = recipe.raw_manufacture_cost || recipe.isk || 0;
              const bakeFeeTotal = bakeFeePer * quantity;
              const materialValue = calculateMaterialValue(recipe.materials || {});
              
              return <li key={name}>
                {quantity} {name}
                <ul>
                  <li>{t('common:components.labels.bakeFee')} {numberWithCommas(bakeFeeTotal)} ({quantity}*{numberWithCommas(bakeFeePer)})</li>
                  <li>{t('common:components.labels.directMaterialsValue')} {numberWithCommas(materialValue * quantity)}</li>
                  
                  <Toggle
                    recipe={recipe}
                    quantity={quantity}
                  >
                    <MaterialList 
                      materials={recipe.materials}
                      recipe={recipe}
                      isk={recipe.raw_manufacture_cost || recipe.isk}
                      blueprintIsk={recipe.blueprint_price}
                    />
                  </Toggle>
                </ul>
              </li>;
            } else if (recipe && recipe.ship_type) {
              
              // Handle ships used as materials
              return <li key={name}>
                {quantity} {name}
                <Toggle
                  recipe={recipe}
                  quantity={quantity}
                >
                  <MaterialList 
                    materials={recipe.materials}
                    recipe={recipe}
                    isk={recipe.raw_manufacture_cost || recipe.isk}
                    blueprintIsk={recipe.blueprint_price}
                  />
                </Toggle>
              </li>;
            } else {
              // Handle regular materials

              return <li key={name}>
                {numberWithCommas(quantity)} {name}
              </li>;
            }
          }
          return null;
        })}
      </ul>
    </li>}
  </ul>
}

export function TransactButton(props) {
  let context = useContext(GlobalInfo);
  const { t } = useTranslation();
  const { showPrompt } = usePromptModalContext();

  let action = async function (type, account, title, help_text) {
    const amountStr = await showPrompt({ title, message: help_text });
    if (!amountStr) {
      return
    }
    let amount = parseNumber(amountStr);
    if (isNaN(amount)) {
      toast.error(t('common:errors.parseError', { input: amountStr }));
      return;
    }

    if (amount < 0 && (type !== ISK_ADJUST && type !== PANDA_ADJUST)) {
      toast.error(t('common:errors.noNegativeNumbers'));
      return;
    }

    return modifyBalance(context.userInfo, user.id, account, type, amount).then((ret) => {
      if(account === ACCOUNTS.CORP_ISK_WITHDRAW) {
        postToDiscord({
          channel: 'WALLET_LOG',
          content: t('common:transactions.messages.withdrawalLogged', {
            name: context.userInfo.name,
            amount: numberWithCommas(amount),
            newBalance: numberWithCommas(ret.newBalanceIsk)
          })
        })
      }

      toast.success(<div>
        {t('common:transactions.messages.complete')}
        <ul>
          <li>{numberWithCommas(ret.amountIsk)} ISK</li>
          <li>{numberWithCommas(ret.amountPanda)} Panda</li>
        </ul>
      </div>)
    }).catch((err) => {
      toast.error(err.toString())
    });
  };

  let user = props.user;
  return <div className={"btn-group"}>
    <DropdownButton id="dropdown-basic-button" title={t('common:buttons.transact')} size={"sm"} variant={"primary"} alignRight={true}>
      <DropdownSubmenu title={t('common:transactions.menu.bill')}>
        <Dropdown.Item onClick={() => action(PANDA_THEN_ISK, ACCOUNTS.CORP_SHIP_SALE,
          t('common:transactions.menu.billForShip'),
          t('common:transactions.prompts.billAmount', { name: user.name })
        )}>{t('common:transactions.menu.billForShip')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(PANDA_THEN_ISK, ACCOUNTS.CORP_FITTING_SALE,
          t('common:transactions.menu.billForFittings'),
          t('common:transactions.prompts.billAmount', { name: user.name })
        )}>{t('common:transactions.menu.billForFittings')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(PANDA_THEN_ISK, ACCOUNTS.CORP_LOOT_SALE,
          t('common:transactions.menu.billForLoot'),
          t('common:transactions.prompts.billAmountSimple', { name: user.name })
        )}>{t('common:transactions.menu.billForLoot')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(PANDA_THEN_ISK, ACCOUNTS.CORP_MATERIAL_SALE,
          t('common:transactions.menu.billForMaterials'),
          t('common:transactions.prompts.billAmountSimple', { name: user.name })
        )}>{t('common:transactions.menu.billForMaterials')}</Dropdown.Item>
      </DropdownSubmenu>
      <Dropdown.Divider/>
      <Dropdown.Item onClick={() => action(PANDA_CREDIT, ACCOUNTS.CORP_SRP,
        t('common:transactions.menu.creditForSrp'),
        t('common:transactions.prompts.creditSrpAmount', { name: user.name })
      )}>{t('common:transactions.menu.creditForSrp')}</Dropdown.Item>
      <Dropdown.Divider/>
      <Dropdown.Item onClick={() => action(ISK_DEBIT, ACCOUNTS.CORP_ISK_WITHDRAW,
        t('common:transactions.menu.recordWithdrawal'),
        t('common:transactions.prompts.withdrawalAmount', { name: user.name, balance: numberWithCommas(user.balanceIsk) })
      )}>{t('common:transactions.menu.recordWithdrawal')}</Dropdown.Item>
      <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_ISK_DEPOSIT,
        t('common:transactions.menu.recordDeposit'),
        t('common:transactions.prompts.depositAmount', { name: user.name })
      )}>{t('common:transactions.menu.recordDeposit')}</Dropdown.Item>
      <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_LOOT_DEPOSIT,
        t('common:transactions.menu.recordDepositLoot'),
        t('common:transactions.prompts.depositLootAmount', { name: user.name })
      )}>{t('common:transactions.menu.recordDepositLoot')}</Dropdown.Item>
      <Dropdown.Divider/>
      <DropdownSubmenu title={t('common:transactions.menu.pay')}>
        <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_PAYMENT_REFINER,
          t('common:transactions.menu.payForRefining'),
          t('common:transactions.prompts.payAmount', { name: user.name })
        )}>{t('common:transactions.menu.payForRefining')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_PAYMENT_LOGISTICS,
          t('common:transactions.menu.payForLogistics'),
          t('common:transactions.prompts.payAmount', { name: user.name })
        )}>{t('common:transactions.menu.payForLogistics')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_PAYMENT_INVENTORY,
          t('common:transactions.menu.payForMaterials'),
          t('common:transactions.prompts.payAmount', { name: user.name })
        )}>{t('common:transactions.menu.payForMaterials')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_PAYMENT_BLUEPRINT,
          t('common:transactions.menu.payForBlueprint'),
          t('common:transactions.prompts.payAmount', { name: user.name })
        )}>{t('common:transactions.menu.payForBlueprint')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_PAYMENT_FITTINGS,
          t('common:transactions.menu.payForFittings'),
          t('common:transactions.prompts.payAmount', { name: user.name })
        )}>{t('common:transactions.menu.payForFittings')}</Dropdown.Item>
        <Dropdown.Item onClick={() => action(ISK_CREDIT, ACCOUNTS.CORP_PAYMENT_MISC,
          t('common:transactions.menu.payForOther'),
          t('common:transactions.prompts.payAmount', { name: user.name })
        )}>{t('common:transactions.menu.payForOther')}</Dropdown.Item>
      </DropdownSubmenu>
      <Dropdown.Divider/>
      <Dropdown.Item onClick={() => action(ISK_ADJUST, ACCOUNTS.ADJUSTMENT,
        t('common:transactions.menu.adjustIsk'),
        t('common:transactions.prompts.adjustIskAmount', { name: user.name, balance: numberWithCommas(user.balanceIsk) })
      )}>{t('common:transactions.menu.adjustIsk')}</Dropdown.Item>
      <Dropdown.Item onClick={() => action(PANDA_ADJUST, ACCOUNTS.ADJUSTMENT,
        t('common:transactions.menu.adjustPanda'),
        t('common:transactions.prompts.adjustPandaAmount', { name: user.name, balance: numberWithCommas(user.balancePanda) })
      )}>{t('common:transactions.menu.adjustPanda')}</Dropdown.Item>
    </DropdownButton>
  </div>
}